import * as THREE from 'three';
import { DAYNIGHT, LIGHTING, NIGHT } from '../config';
import type { SkyUniforms } from './scene';

// Day-night cycle. Advances an in-game clock, arcs the sun across the sky, and blends the sky / sun /
// fog / ambient colours from dawn -> noon -> dusk -> night. dayCount + the fractional time give
// totalDays, which the tree regrowth reads. Short day + very short night for testing (see config).

// Keyframe colours per channel: [night, day, dusk]. Sky-dome colours are LINEAR (the dome renders into
// the graded/linear target); light / fog / hemisphere colours stay sRGB (three converts them).
const lin = (hex: number): THREE.Color => new THREE.Color(hex).convertSRGBToLinear();
const srgb = (hex: number): THREE.Color => new THREE.Color(hex);

interface KF { night: THREE.Color; day: THREE.Color; dusk: THREE.Color; }

const ZENITH: KF = { night: lin(0x070d1c), day: lin(0x3f78d8), dusk: lin(0x4a3f78) };
const HORIZON: KF = { night: lin(0x111a30), day: lin(0x9ec2e0), dusk: lin(0xff9a4d) };
const SUN_SKY: KF = { night: lin(0x16224a), day: lin(0xfff2da), dusk: lin(0xff7a2a) }; // dome sun disc
const CLOUD: KF = { night: lin(0x1a2238), day: lin(0xffffff), dusk: lin(0xe7b48a) };
const FOG: KF = { night: srgb(0x06080f), day: srgb(0x9ec2e0), dusk: srgb(0xe8884a) }; // night fog is near-dark so distant land does not lift to a grey band; day matches the dome HORIZON
const SUN_LIGHT: KF = { night: srgb(0x20305a), day: srgb(0xfff0d6), dusk: srgb(0xff8a3a) };
const HEMI_SKY: KF = { night: srgb(0x223052), day: srgb(0x9fbfe0), dusk: srgb(0xc98a5a) };
const HEMI_GND: KF = { night: srgb(0x10160f), day: srgb(0x39492a), dusk: srgb(0x3a2a1a) };

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
// out = night, lerped toward day (dayAmt), then toward dusk (duskAmt). Mutates + returns out.
function blend(out: THREE.Color, kf: KF, dayAmt: number, duskAmt: number): void {
  out.copy(kf.night).lerp(kf.day, dayAmt).lerp(kf.dusk, duskAmt);
}

export class DayNight {
  dayCount = 0;
  frozen = false; // F2 admin: hold the clock at a set time of day for testing (no auto-advance)
  readonly sunDir = new THREE.Vector3(0, 1, 0);
  private t = DAYNIGHT.startSeconds;
  private dayAmt = 1; // 0 at night, 1 in full day (drives the night grade + env dimming)

  constructor(
    private readonly sun: THREE.DirectionalLight,
    private readonly hemi: THREE.HemisphereLight,
    private readonly scene: THREE.Scene,
    private readonly sky: SkyUniforms,
  ) {
    this.apply();
  }

  private get cycle(): number { return DAYNIGHT.daySeconds + DAYNIGHT.duskSeconds + DAYNIGHT.nightSeconds + DAYNIGHT.dawnSeconds; }
  get cycleSeconds(): number { return this.cycle; }
  // Fractional in-game days elapsed (drives tree regrowth).
  get totalDays(): number { return this.dayCount + this.t / this.cycle; }
  // 0..1 within the current cycle (for a HUD / debug readout if wanted).
  get phase(): number { return this.t / this.cycle; }

  // F2 admin: jump the clock to a set point in the cycle (seconds) and hold it there.
  setTime(seconds: number): void {
    this.t = Math.max(0, Math.min(this.cycle - 0.001, seconds));
    this.frozen = true;
    this.apply();
  }

  // Total world-clock seconds since day 0 (for the server drift check).
  get clockSeconds(): number { return this.dayCount * this.cycle + this.t; }

  // Server clock sync (M1): set the clock from the world's shared elapsed time WITHOUT freezing it.
  // A locally frozen clock (F2 admin hold) wins -- the owner is testing lighting, don't fight them.
  syncTo(totalSeconds: number): void {
    if (this.frozen) return;
    this.dayCount = Math.max(0, Math.floor(totalSeconds / this.cycle));
    this.t = Math.max(0, totalSeconds - this.dayCount * this.cycle);
    this.apply();
  }
  // Re-apply lighting from the current clock (so live night-setting edits show while frozen).
  refresh(): void { this.apply(); }
  // 0 at night .. 1 in full day. Main drives the night grade darkening from 1 - dayAmount.
  get dayAmount(): number { return this.dayAmt; }

  update(dt: number): void {
    if (this.frozen) { this.apply(); return; } // held at a set time; still re-apply so live night-setting edits show
    this.advance(dt * DAYNIGHT.timeScale);
  }

  // Jump the clock forward (whole or fractional days) -- handy for testing regrowth without waiting.
  skipDays(days: number): void {
    this.advance(days * this.cycle);
  }

  private advance(seconds: number): void {
    this.t += seconds;
    while (this.t >= this.cycle) { this.t -= this.cycle; this.dayCount++; }
    while (this.t < 0) { this.t += this.cycle; this.dayCount = Math.max(0, this.dayCount - 1); }
    this.apply();
  }

  private apply(): void {
    // FOUR explicit segments (owner pacing): full day, dusk ramp, night, dawn ramp. The sun-elevation
    // proxy e drives every colour/light blend downstream: the full-day window HOLDS e high enough that
    // dayAmt stays 1 (no creeping golden hour eating the 20 minutes), and the dusk/dawn windows carry
    // the entire transition.
    const D = DAYNIGHT.daySeconds, K = DAYNIGHT.duskSeconds, N = DAYNIGHT.nightSeconds, W = DAYNIGHT.dawnSeconds;
    const ease = (v: number): number => v * v * (3 - 2 * v);
    let e: number, az: number;
    if (this.t < D) {
      const u = this.t / D;
      e = 0.30 + 0.70 * Math.sin(Math.PI * u); // 0.30 at the day's edges keeps dayAmt pinned at 1
      az = -1.3 + 2.4 * u;                     // sweep east -> west across the day
    } else if (this.t < D + K) {
      const v = ease((this.t - D) / K);        // DUSK: sun sinks 0.30 -> -0.30 (the whole transition)
      e = 0.30 - 0.60 * v;
      az = 1.1 + 0.3 * v;
    } else if (this.t < D + K + N) {
      const w = (this.t - D - K) / N;          // NIGHT: below the horizon, deepest mid-night
      e = -0.30 - 0.15 * Math.sin(Math.PI * w);
      az = 1.4 + 0.6 * w;
    } else {
      const v = ease((this.t - D - K - N) / W); // DAWN: mirror of dusk, -0.30 -> 0.30
      e = -0.30 + 0.60 * v;
      az = 2.0 + 0.3 * v;
    }
    const phi = e * Math.PI * 0.45; // elevation angle (max ~81deg at noon)
    const horiz = Math.cos(phi);
    this.sunDir.set(Math.cos(az) * horiz, Math.sin(phi), Math.sin(az) * horiz).normalize();

    const dayAmt = smoothstep(0.02, 0.28, e);                          // 0 at/below horizon, 1 sun up
    const duskAmt = smoothstep(-0.06, 0.06, e) * (1 - smoothstep(0.06, 0.30, e)); // warm low sun
    this.dayAmt = dayAmt;

    // Sky dome.
    blend(this.sky.uZenith.value, ZENITH, dayAmt, duskAmt);
    blend(this.sky.uHorizon.value, HORIZON, dayAmt, duskAmt);
    blend(this.sky.uSunColor.value, SUN_SKY, dayAmt, duskAmt);
    blend(this.sky.uCloud.value, CLOUD, dayAmt, duskAmt);
    this.sky.uSunDir.value.copy(this.sunDir);

    // Fog + background.
    const fog = this.scene.fog as THREE.Fog | null;
    if (fog && fog.color) blend(fog.color, FOG, dayAmt, duskAmt);
    if (this.scene.background instanceof THREE.Color) blend(this.scene.background, FOG, dayAmt, duskAmt);

    // Sun light: colour + intensity (off at night), shadows only while it is up.
    blend(this.sun.color, SUN_LIGHT, dayAmt, duskAmt);
    this.sun.intensity = LIGHTING.sunIntensity * dayAmt;
    this.sun.castShadow = e > 0.04;

    // Hemisphere ambient: dim + bluish at night, full by day. The night FLOORS scale with TONIGHT's
    // moon (rolled per game day in main): a full moon lifts them so silhouettes read, a new moon
    // leaves the config floors as-is (near pitch black).
    const moonLift = 1 + (NIGHT.moonAmbient - 1) * NIGHT.moonTonight;
    blend(this.hemi.color, HEMI_SKY, dayAmt, duskAmt);
    blend(this.hemi.groundColor, HEMI_GND, dayAmt, duskAmt);
    const hemiFloor = DAYNIGHT.nightHemi * moonLift;
    this.hemi.intensity = hemiFloor + (LIGHTING.hemiIntensity - hemiFloor) * dayAmt;

    // Environment (PBR ambient) bottoms out near zero at night so unlit ground is genuinely black -- a
    // torch or other light is the only way to see (config DAYNIGHT.nightEnv / nightHemi).
    const envFloor = DAYNIGHT.nightEnv * moonLift;
    this.scene.environmentIntensity = envFloor + (LIGHTING.envIntensity - envFloor) * dayAmt;
  }
}
